import { useEffect, useState } from 'react'
import { useRoadmapLoaders, useVehicleLoaders, useDriverLoaders, Roadmap, Vehicle, Driver, DeliveryRoadmap } from '../../../logistics'
import { useDeliveryLoaders, Delivery, DeliveryMethod, DeliveryStatus } from '../../../deliveries'
import { useSystemStateHooks } from '../../../system'
import { useManagerState } from '../../../manager'
import { isToday } from '../../utils/dashboardUtils'

export type LogisticsData = {
    todayRoadmaps: Roadmap[]
    roadmapLinks: DeliveryRoadmap[]
    shipmentDeliveries: Delivery[]
    vehicles: Vehicle[]
    drivers: Driver[]
}

// enabled: false when no visible card needs this data (it isn't loaded)
export const useLogisticsData = (enabled = true) => {
    const [loading, setLoading] = useState(enabled)
    const [error, setError] = useState(false)
    const [data, setData] = useState<LogisticsData | null>(null)

    const { companyId } = useSystemStateHooks()
    const { currentBranch } = useManagerState()
    const roadmapLoaders = useRoadmapLoaders()
    const vehicleLoaders = useVehicleLoaders()
    const driverLoaders = useDriverLoaders()
    const deliveryLoaders = useDeliveryLoaders()

    useEffect(() => {
        if (!enabled) return
        setLoading(true)
        const load = async () => {
            try {
                const id = companyId
                const branchId = currentBranch?.id

                const branchFilter = branchId ? [{ prop: 'branchId', value: branchId }] : []

                const [allRoadmaps, shipmentsRes, vehicles, drivers] = await Promise.all([
                    branchId
                        ? roadmapLoaders(id).loadBranchRoadmaps(branchId)
                        : roadmapLoaders(id).loadAllRoadmaps(),
                    // Always from the db: the cached deliveries may be only some of them (e.g. the ones of an opened roadmap)
                    deliveryLoaders(id).loadDeliveriesBatch({
                        valueFilters: [{ prop: 'method', value: DeliveryMethod.SHIPMENT }, ...branchFilter]
                    }, false),
                    vehicleLoaders(id).loadAllVehicles(),
                    driverLoaders(id).loadAllDrivers()
                ])

                const todayRoadmaps = (allRoadmaps as Roadmap[]).filter(r => isToday(r.date))

                const roadmapLinks = todayRoadmaps.length > 0
                    ? await roadmapLoaders(id).loadRoadmapsDeliveriesLinks(todayRoadmaps.map(r => r.id))
                    : []

                const shipmentDeliveries = shipmentsRes.dataTypes.filter(
                    d => !d.cancelled && (d.deliveryStatus === DeliveryStatus.PENDING || d.deliveryStatus === DeliveryStatus.PARTIALLY_DELIVERED)
                )

                setData({
                    todayRoadmaps,
                    roadmapLinks: roadmapLinks as DeliveryRoadmap[],
                    shipmentDeliveries,
                    vehicles: (vehicles as Vehicle[]).filter(v => !v.archived),
                    drivers: (drivers as Driver[]).filter(d => !d.archived)
                })
            } catch {
                setError(true)
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [enabled])

    return { loading, error, data }
}
